import React, { useEffect, useMemo, useRef, useState } from "react";
import "../Analytics.css";

export const DEFAULT_BASELINE = ["PRIOR"];

const FIRST_YEAR = 2024;

const PRESET_OPTIONS = [
  { value: "MTD", label: "MTD", hint: "last month" },
  { value: "QUARTERLY", label: "QUARTERLY", hint: "last quarter" },
  { value: "HALF_YEARLY", label: "HALF_YEARLY", hint: "last half-year" },
  { value: "YTD", label: "YTD", hint: "previous year-to-date" },
  { value: "YEARLY", label: "YEARLY", hint: "previous year" },
  { value: "WEEKLY", label: "WEEKLY", hint: "last week" },
];

const buildGroups = (currentYear) => {
  const lastYear = currentYear - 1;
  const years = [];
  for (let y = lastYear; y >= Math.min(FIRST_YEAR, lastYear); y -= 1) {
    years.push({ value: String(y), label: String(y) });
  }
  const periods = ["Q1", "Q2", "Q3", "Q4", "H1", "H2"].map((p) => ({
    value: `${lastYear}-${p}`,
    label: `${lastYear}-${p}`,
  }));
  return [
    { title: "Years", options: years },
    { title: "Quarters / halves", options: periods },
    {
      title: "Previous equivalent period",
      options: PRESET_OPTIONS.map((o) => ({ value: o.value, label: `${o.label} — ${o.hint}` })),
    },
    {
      title: "Default",
      options: [{ value: "PRIOR", label: "PRIOR — All history before the current period" }],
    },
  ];
};

const summarize = (selected) => {
  if (selected.length === 0) return "None (param omitted)";
  if (selected.length === 1 && selected[0] === "PRIOR") return "All history before current period";
  if (selected.length <= 3) return selected.join(" + ");
  return `${selected.length} selected`;
};

// PRIOR overlaps every other option, so selecting it clears the rest and
// selecting anything else clears it.
export const toggleBaseline = (selected, value) => {
  if (value === "PRIOR") return selected.includes("PRIOR") ? [] : ["PRIOR"];
  const without = selected.filter((v) => v !== "PRIOR");
  return without.includes(value) ? without.filter((v) => v !== value) : [...without, value];
};

const BaselineSelect = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);
  const groups = useMemo(() => buildGroups(new Date().getFullYear()), []);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    const onKeyDown = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <div className="spike-filter" ref={rootRef}>
      <span className="spike-filter-label" id="spike-baseline-label">Baseline</span>
      <button
        type="button"
        className="spike-filter-trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-labelledby="spike-baseline-label"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="spike-filter-trigger-text">{summarize(value)}</span>
        <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="spike-filter-popover" role="listbox" aria-multiselectable="true" aria-labelledby="spike-baseline-label">
          {groups.map((group) => (
            <fieldset key={group.title} className="spike-filter-group">
              <legend>{group.title}</legend>
              {group.options.map((opt) => (
                <label key={opt.value} className="spike-filter-option">
                  <input
                    type="checkbox"
                    checked={value.includes(opt.value)}
                    onChange={() => onChange(toggleBaseline(value, opt.value))}
                  />
                  <span>{opt.label}</span>
                </label>
              ))}
            </fieldset>
          ))}
        </div>
      )}
    </div>
  );
};

export default BaselineSelect;
